<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import type { AssistantInstruction } from '@/teoCore/TeoApi';
  import type {
    AutoAiHistoryEntry,
    AutoEndReason,
    AutoSuggestionState,
  } from '@/teoCore/text/AIHandler/AutoSuggestionHandler';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import BaseCard from '@/ui/elements/card/BaseCard.svelte';
  import CardHeader from '@/ui/elements/card/CardHeader.svelte';
  import InfoCard from '@/ui/elements/card/InfoCard.svelte';
  import VersionScore from '@/ui/elements/version/VersionScore.svelte';
  import { useAnalysisState } from '@/utils/teo-context/teo-atoms/analysis';

  import { useNotificationActions } from '../../dialogs/use-notification.svelte';
  import HistoryItem from '../../history/HistoryItem.svelte';

  const { teoText, instruction }: { teoText: TeoText; instruction?: AssistantInstruction } =
    $props();

  let auto = $derived(teoText.ai.auto);
  let AS = $derived(auto.store);
  let analysisState = useAnalysisState();
  const { addNotification } = useNotificationActions();

  onMount(() => {
    auto.history.ensureSessionBaseline();
  });

  onDestroy(() => {
    teoText.ai.auto.stop();
    const perfomanceMode = teoText.store.state.performanceMode;
    const view = teoText.store.state.view;
    teoText.isEditable = true;
    if (auto.store.state.activeHistoryId !== null) {
      const first = auto.store.getLatestHistoryEntry();
      if (first) {
        auto.history.selectHistory(first);
      }
    }
    teoText.store.setStateValue('performanceMode', perfomanceMode);
    teoText.store.setStateValue('view', view);
    teoText.diff.hide();
    // create a new version entry in the history
    if (teoText.ai.auto.history.hasHistory()) {
      teoText.history.addHistoryEntryIfChanged();
    }
  });

  // The instruction decides which preset the handler asks for, so without one there is
  // nothing this card could request.
  let canRun = $derived(
    !!instruction?.ai_preset &&
      (($AS.uiHistory.at(0)?.tpi ?? 0) < auto.getTpiTarget() ||
        analysisState.current?.isCurrent === false),
  );

  const END_REASON_TEXT: Record<AutoEndReason, string> = {
    applied: 'Die KI hat den Text angepasst.',
    noSuggestions: 'Die KI hat keine weiteren Verbesserungen für diesen Text.',
    noInstructions:
      'Es sind keine weiteren Aufgaben übrig. Das TPI-Ziel wurde noch nicht erreicht.',
    targetReached: 'Das TPI-Ziel wurde erreicht.',
    tpiUnchanged: 'Die KI konnte den TPI nicht weiter verbessern.',
    maxRuns: 'Die maximale Anzahl an Durchläufen ist erreicht.',
    stopped: 'Die Auto-Optimierung wurde gestoppt.',
  };

  let endReasonText = $derived(
    $AS.endReasonMessage || ($AS.endReason ? END_REASON_TEXT[$AS.endReason] : ''),
  );

  function startLoop(): void {
    // The loop adds its steps on top of the newest version, so it has to start from that text.
    const newest = $AS.uiHistory.at(0);
    if (!newestHistoryActive && newest) auto.history.showHistoryChanges(newest);
    auto.startLoop();
  }

  /** What the loop is busy with, and what a stop it was asked for is waiting for. */
  function loopStatus(state: AutoSuggestionState): string {
    // const run = `Durchlauf ${state.loopRuns + 1}`;
    if (state.stopRequest === 'hard') return `wird gestoppt`;
    if (state.stopRequest === 'soft') return `stoppt nach diesem Durchlauf`;
    return state.phase === 'analyzing' ? `Analyse läuft` : `KI schreibt`;
  }

  let newestHistoryActive = $derived(
    $AS.activeHistoryId === null || $AS.activeHistoryId === $AS.uiHistory.at(0)?.id,
  );

  let selectedItem = $derived(
    ($AS.activeHistoryId && AS.getHistoryById($AS.activeHistoryId)) || $AS.uiHistory.at(0) || null,
  );

  function restoreHistory(qh: AutoAiHistoryEntry): void {
    // Cutting the history in place would drop steps the running loop builds on.
    const canRestoreInPlace = () => !auto.isLooping && !auto.isRunning;
    addNotification({
      title: 'Diese Textversion wieder herstellen?',
      message: 'Spätere Optimierungsschritte werden entfernt.',
      mode: 'warning',
      buttons: [
        {
          label: 'Abbrechen',
          variant: 'secondary',
        },
        {
          label: 'Ja, in neuem Tab',
          variant: 'primary',
          onclick: () => {
            const newTabText = teoText.core.duplicateText(teoText.id);
            if (!newTabText) {
              return;
            }
            teoText.ai.auto.history.cloneHistoryToText(newTabText, qh);
          },
        },
        ...(canRestoreInPlace() ?
          [
            {
              label: 'Ja',
              variant: 'primary' as const,
              onclick: () => {
                if (canRestoreInPlace()) auto.history.restoreHistory(qh);
              },
            },
          ]
        : []),
      ],
    });
  }

  function handleSelect(qh: AutoAiHistoryEntry): void {
    auto.history.showHistoryChanges(qh);
  }

  function getPos(index: number) {
    if ($AS.uiHistory.length <= 1) return 'single';
    if (index === 0) return 'first';
    if (index === $AS.uiHistory.length - 1) return 'last';
    return 'default';
  }
</script>

<BaseCard>
  {#snippet header()}
    <CardHeader cardLabel="KI-Autopilot">
      <CtaButton
        onclick={() => {
          auto.exitAutoMode();
        }}
        variant="secondary"
        icon="Undo"
        disabled={$AS.isLooping}
        tooltip="Zurück zum Performance-Pilot"
      ></CtaButton>
    </CardHeader>
  {/snippet}

  <div class="flex justify-end">
    {#if $AS.isLooping}
      <CtaButton
        onclick={() => auto.stop()}
        variant="secondary"
        icon="Close"
        disabled={$AS.stopRequest === 'hard'}
        tooltip="Auto-Optimierung stoppen">Stopp</CtaButton
      >
    {:else}
      <CtaButton
        onclick={startLoop}
        variant="primary"
        icon="MagicExchange"
        class="min-w-20"
        tooltip="Optimieren, bis der Text das TPI-Ziel erreicht"
        disabled={!canRun || $AS.isRunning}>Starten</CtaButton
      >
    {/if}
  </div>
  {#if endReasonText}
    <InfoCard shortText={endReasonText} dimension="blue"></InfoCard>
  {/if}
  <ul class="fc">
    {#if $AS.isLooping}
      <li>
        <HistoryItem item={{}} pos="single" selected={false} divider="bottom">
          <VersionScore value={0} loading />
          <span class="text-label-sm">{$AS.runningInstructionTitle ?? loopStatus($AS)}</span>
        </HistoryItem>
      </li>
    {/if}
    {#each $AS.uiHistory as item, index (item.id)}
      {@const pos = getPos(index)}
      <li>
        <HistoryItem
          index={$AS.uiHistory.length - index}
          {item}
          {pos}
          onrestore={$AS.uiHistory.length > 1 ? restoreHistory : undefined}
          onclick={handleSelect}
          selected={item === selectedItem}
          divider="none"
        >
          {#if item.tpi}
            <VersionScore value={item.tpi} />
          {/if}
          <span class="text-label-sm">{item.instruction_title ?? ''}</span>
        </HistoryItem>
      </li>
    {/each}
  </ul>
</BaseCard>
